/**
 * Removes a user's data before Firebase Auth account deletion.
 * The callable `purgeAccountData` is the complete path (reports, storage, Firestore, public profile).
 * If that function is not deployed, a client cleanup runs for the records this user can delete.
 */

import { deleteDoc, doc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ref, remove } from "firebase/database";
import { deleteObject, listAll, ref as storageRef } from "firebase/storage";
import { app, db, firestore, storage } from "./firebase";
import { getFollowers, getFollowing, unfollowUser } from "../services/followService";
import { deleteAllContentsForUser, getUserContentCount } from "../features/content/contentCrudService";

export const getTotalUserContentCount = async (uid) => {
  return getUserContentCount(uid);
};

async function deleteStoragePrefix(prefix) {
  const folder = storageRef(storage, prefix);
  const listed = await listAll(folder);
  await Promise.all(listed.items.map((item) => deleteObject(item)));
}

async function clientPurge(uid) {
  const steps = [];

  steps.push(
    deleteAllContentsForUser(uid).catch((error) => {
      console.warn("accountDeletionService: contents", error);
      throw error;
    })
  );

  const followers = await getFollowers(uid).catch(() => []);
  const following = await getFollowing(uid).catch(() => []);
  for (const f of followers) {
    try {
      await unfollowUser(f.userId, uid);
    } catch (error) {
      console.warn("accountDeletionService: follower edge", error);
    }
  }
  for (const f of following) {
    try {
      await unfollowUser(uid, f.userId);
    } catch (error) {
      console.warn("accountDeletionService: following edge", error);
    }
  }

  await Promise.all([
    remove(ref(db, `publicProfiles/${uid}`)).catch((error) => {
      console.warn("accountDeletionService: public profile", error);
    }),
    deleteDoc(doc(firestore, "users", uid)).catch((error) => {
      console.warn("accountDeletionService: firestore profile", error);
    }),
    deleteStoragePrefix(`avatars/${uid}`).catch((error) => {
      console.warn("accountDeletionService: avatars", error);
    }),
    deleteStoragePrefix(`branding/sidebar-logo/${uid}`).catch((error) => {
      console.warn("accountDeletionService: branding", error);
    }),
  ]);

  await Promise.all(steps);
  await remove(ref(db, `users/${uid}`));
}

/**
 * Deletes stored account data. Call while the user is still authenticated.
 * @param {string} uid
 */
export const purgeUserAccountData = async (uid) => {
  if (!uid) return;

  const purge = httpsCallable(getFunctions(app), "purgeAccountData");
  try {
    await purge();
    return;
  } catch (error) {
    const code = String(error?.code || "");
    const missing = code.includes("not-found") || code.includes("unavailable");
    if (!missing) {
      throw new Error(error?.message || "Account cleanup failed. Your login was not deleted. Try again.");
    }
  }

  await clientPurge(uid);
  console.warn(
    "purgeAccountData is not deployed. Client cleanup removed content, follows, the profile, Firestore interests, and storage the account can delete. Reports still need the Cloud Function."
  );
};
